import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import socket from "../../socket";
import { API_URL } from "../../config/constants";
import avatarMap from "../../assets/avatars";
import { pathLogin, pathPatientHome, pathPatientActivityPlan, pathPatientCaregiverSupport } from "../../config/constants";
import {
  Sidebar, PatientPreview, PreviewAvatar, PreviewCode,
  Nav, NavItem, NavBadge, SidebarBottom, SidebarLink,
} from "./styles";

const IconHome = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconSummary = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconChat = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const IconForm = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);
const IconSettings = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const IconLogout = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IconGlobe = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const PatientSidebar = ({ activeNav = "home" }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const avatar = avatarMap[user.avatar_url] || Object.values(avatarMap)[0];
  const [unread, setUnread] = useState(0);

  // En Caregiver Support los está leyendo: no hay nada que anunciar.
  const onChatScreen = activeNav === "caregiver";

  useEffect(() => {
    if (onChatScreen) { setUnread(0); return; }
    fetch(`${API_URL}/me/therapist`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((d) => setUnread(Number(d?.unread_count) || 0))
      .catch(() => {});
  }, [onChatScreen]);

  useEffect(() => {
    const onMessage = (msg) => {
      if (onChatScreen || msg.receiver_id !== user.id) return;
      setUnread((n) => n + 1);
    };
    socket.on("receiveMessage", onMessage);
    return () => socket.off("receiveMessage", onMessage);
  }, [onChatScreen, user.id]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    history.push(pathLogin);
  };

  return (
    <Sidebar>
      <PatientPreview>
        <PreviewAvatar src={avatar} alt="avatar" />
        <PreviewCode>{user.username || t("patientHome.patient")}</PreviewCode>
      </PatientPreview>

      <Nav>
        <NavItem $active={activeNav === "home"} onClick={() => history.push(pathPatientHome)}>
          <IconHome />{t("patientNav.home")}
        </NavItem>
        <NavItem $active={activeNav === "activity-plan"} onClick={() => history.push(pathPatientActivityPlan)}>
          <IconCalendar />{t("nav.activityPlan")}
        </NavItem>
        <NavItem $active={activeNav === "summary"}>
          <IconSummary />{t("nav.summary")}
        </NavItem>
        <NavItem
          $active={activeNav === "caregiver"}
          onClick={() => history.push(pathPatientCaregiverSupport)}
        >
          <IconChat />{t("nav.caregiverSupport")}
          {unread > 0 && <NavBadge>{unread > 99 ? "99+" : unread}</NavBadge>}
        </NavItem>
        <NavItem $active={activeNav === "form"}>
          <IconForm />{t("patientNav.form")}
        </NavItem>
      </Nav>

      <SidebarBottom>
        <SidebarLink><IconSettings />{t("nav.settings")}</SidebarLink>
        <SidebarLink onClick={logout}><IconLogout />{t("nav.logout")}</SidebarLink>
        <SidebarLink href="https://vippstar.eu" target="_blank" rel="noreferrer">
          <IconGlobe />vippstar.eu
        </SidebarLink>
      </SidebarBottom>
    </Sidebar>
  );
};

export default PatientSidebar;
